
import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import {
    collection,
    query,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    orderBy,
    Timestamp
} from 'firebase/firestore';

export interface ETFHolding {
    asset: string;
    weight: number;
}

export interface ETF {
    id: string;
    name: string;
    ticker: string;
    description: string;
    basePrice: number;
    currentPrice: number;
    roi: number;
    aum: number;
    category: string;
    holdings: ETFHolding[];
    status: 'Active' | 'Inactive';
    vitals: number[];
    createdAt: Timestamp;
    updatedAt: Timestamp;
}

export interface ETFInput {
    name: string;
    ticker: string;
    description: string;
    basePrice: number;
    currentPrice: number;
    roi: number;
    aum: number;
    category: string;
    holdings: ETFHolding[];
    status: 'Active' | 'Inactive';
    vitals: number[];
}

export function useETF() {
    const [etfs, setEtfs] = useState<ETF[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, 'etf_products'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => ({
                id: d.id,
                ...d.data()
            })) as ETF[];
            setEtfs(items);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const addETF = async (input: ETFInput) => {
        await addDoc(collection(db, 'etf_products'), {
            ...input,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    };

    const updateETF = async (id: string, input: Partial<ETFInput>) => {
        const docRef = doc(db, 'etf_products', id);
        await updateDoc(docRef, {
            ...input,
            updatedAt: serverTimestamp()
        });
    };

    const deleteETF = async (id: string) => {
        await deleteDoc(doc(db, 'etf_products', id));
    };

    return { etfs, loading, addETF, updateETF, deleteETF };
}
